/**
 * 投稿直後の解析パイプライン。
 *
 * 1. 本文を Ruri-v3 で 1 回だけ埋め込む
 * 2. 行動プロトタイプと比較 → 行動タイプ
 * 3. 認知プロトタイプと比較 → 8 認知機能の per-post 正規化スコア
 * 4. 今日の questLog を読み込み / 新規、該当 growth / maintenance の currentCount を +1、
 *    restraint の forbiddenActionTypes 該当なら failed マーク
 * 5. analysis を更新:
 *    - cognitiveScores を α=0.97 でブレンド (新しい投稿が少しずつ寄せる)
 *    - rpgStats に ACTION_WEIGHTS を加算して合計 100 に正規化
 *    - archetype を新 cognitiveScores から再判定
 * 6. 更新結果を呼び出し元に返す
 */

import type { Agent } from '@atproto/api';
import { COL } from './collections';
import type { ActionType, Archetype, CogFunction, CognitiveScores, DiagnosisResult, JobLevelState, PlayerLevelState, Quest, QuestTemplate, StatVector } from '@aozoraquest/core';
import {
  ACTIVITY_HISTORY_LIMIT,
  ACTIVITY_PREVIEW_LENGTH,
  COGNITIVE_BLEND_ALPHA,
  DAILY_BONUS_DAY_MARGIN_FACTOR,
  DEFAULT_QUEST_TEMPLATES,
  JOB_CHANGE_STREAK_THRESHOLD as CORE_JOB_CHANGE_STREAK_THRESHOLD,
  JOB_XP_CURVE,
  XP_REWARDS,
  cognitiveToRpg,
  determineArchetype,
  jobLevelFromXp,
} from '@aozoraquest/core';
import { classifyFromVec, type ActionCategory } from './action-classifier';
import { classifyCognitiveFromVec } from './cognitive-classifier';
import { Embedder, EmbedderUnsupportedError } from './embedder';
import { getRecord, putRecord } from './atproto';
import { serverClaimXp } from './world-server';
import { claimPostXp } from './pending-claims';
import { deriveActionTypes, type PostStructure } from './structural-action';

export interface QuestLogEntry {
  id: string;
  templateId: string;
  type: 'growth' | 'maintenance' | 'restraint';
  targetStat: 'atk' | 'def' | 'agi' | 'int' | 'luk';
  requiredCount: number;
  currentCount: number;
  completed: boolean;
  xpAwarded?: number;
}

/** 投稿 1 件の分類履歴 (透明性表示用: なぜクエストが進んだかを後から見るため) */
export interface ActivityEntry {
  /** 発生時刻 (ISO) */
  at: string;
  /** 投稿本文の先頭 (最大 60 字)。プライバシー配慮で全文は記録しない。 */
  preview: string;
  /** 分類された主行動タイプ (text classifier 由来)。分類不能なら null。 */
  action: string | null;
  /** この投稿に該当した全 ActionType (テキスト分類 + 構造判定の合成)。
   *  例: ['opinion_post', 'quote_with_opinion'] のように複数つく場合がある。 */
  actionTypes?: string[];
  /** この投稿で +1 された quest の templateId 一覧。 */
  incremented: string[];
}

export interface QuestLogRecord {
  date: string;
  quests: QuestLogEntry[];
  /** 今日分類された投稿の履歴 (古い順)。最新 50 件までを保持。 */
  activity?: ActivityEntry[];
  /** @deprecated クエスト完了では XP が増えない (2026-07-27)。過去ログの互換のために残す。 */
  totalXpGained?: number;
  updatedAt: string;
}

export interface ProcessResult {
  action: ActionCategory | null;
  incremented: string[];
  completed: string[];
  /** @deprecated 完了したクエストの報酬値の合計。**成長には効かない** (2026-07-27)。 */
  xpGained: number;
  updatedRpgStats?: StatVector | undefined;
  updatedCognitive?: CognitiveScores | undefined;
  jobLevel?: JobLevelState | undefined;
  playerLevel?: PlayerLevelState | undefined;
  jobLeveledUp?: { from: number; to: number } | undefined;
  /** 現 archetype と異なる判定が出ている場合の候補と連続回数。 */
  pendingArchetype?: Archetype | undefined;
  pendingArchetypeStreak?: number | undefined;
}

/**
 * UI のバナー表示閾値。tuning.JOB_CHANGE_STREAK_THRESHOLD の別名を再 export
 * (UI が本ファイルからまとめて import できるように)。
 */
export const JOB_CHANGE_STREAK_THRESHOLD = CORE_JOB_CHANGE_STREAK_THRESHOLD;

const COGNITIVE_FUNCTIONS: CogFunction[] = ['Ni', 'Ne', 'Si', 'Se', 'Ti', 'Te', 'Fi', 'Fe'];

function todayDateString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function templateById(id: string): QuestTemplate | undefined {
  return DEFAULT_QUEST_TEMPLATES.find((t) => t.id === id);
}

export async function processSelfPost(
  agent: Agent,
  did: string,
  text: string,
  structure?: PostStructure,
  /** この投稿の URI。XP 申告の冪等キーに使う (#534)。省略すると XP は申告されない。 */
  postUri?: string,
): Promise<ProcessResult> {
  const trimmed = text.trim();
  /** XP 申告。analysis の書き込みが終わってから走らせる (edge の待ちで PDS 更新を落とさない)。 */
  let claim: () => Promise<void> = async () => {};
  const empty: ProcessResult = { action: null, incremented: [], completed: [], xpGained: 0 };
  if (trimmed.length === 0) return empty;

  // 投稿のたびに走る解析。**端末内モデルは「埋め込み 1 個」だけ**ロードする。
  //
  // 以前は embedder + cognitive の 2 モデルを同時にロードしていたが、iOS Safari は
  // 2 モデル同時ロードの瞬間メモリスパイクに耐えられず (8GB の iPhone でも)、投稿直後に
  // WebContent プロセスが jetsam で破棄され画面全体がリロードされる (実機切り分けで確定:
  // モデル推論を切ると落ちない / 2 モデルは落ちる / 1 モデルは通る)。
  //
  // そこで行動分類・認知スコアとも **埋め込みベクトルの prototype cosine** で求め、
  // cognitive の分類器は投稿パスでは使わない。認知スコアの精度は /me のフル診断
  // (runDiagnosis) 側の分類器で担保し、投稿ごとの更新は α=COGNITIVE_BLEND_ALPHA の
  // 微小 nudge なので prototype で十分。embedder は専用ローカルインスタンスにして共有
  // singleton (診断 / 相性ランキング / TL 認知分析) を巻き込まず、推論後に dispose する
  // (GPU buffer を destroy)。埋め込みが使えない時 (WebGPU 非対応 / 重みの取得失敗 / GPU の
  // メモリ不足など) は解析 (行動分類・認知の nudge) だけを飛ばし、クエスト記録と XP 申告は続ける。
  const embedder = new Embedder();
  let action: ActionCategory | null = null;
  let postCognitive: CognitiveScores | null = null;
  try {
    const vec = await embedder.embed(trimmed);
    action = (await classifyFromVec(vec)).action;                    // 行動: prototype cosine
    postCognitive = await classifyCognitiveFromVec(vec, embedder);   // 認知: prototype cosine
  } catch (e) {
    // 非対応端末は毎投稿で起きる想定内なので黙る。それ以外は原因を追えるよう warn だけ残す。
    if (!(e instanceof EmbedderUnsupportedError)) console.warn('post embedding failed; analysis skipped', e);
  } finally {
    embedder.dispose();
  }

  const actionType = action ? (action as ActionType) : null;
  // テキスト分類 + 構造から該当 ActionType 集合を合成。
  // structure が無いケース (旧 API) でも textLength は trimmed から導出する。
  const fallbackStructure: PostStructure = structure ?? {
    isReply: false,
    isReplyToSelf: false,
    isQuote: false,
    textLength: trimmed.length,
  };
  const actionTypes = deriveActionTypes(action, fallbackStructure);

  // 3) questLog の更新 (常に記録: 分類されなかった投稿も activity に残すことで「なぜ進まないか」が分かる)
  const incremented: string[] = [];
  const completed: string[] = [];
  let xpGained = 0;
  {
    const date = todayDateString();
    const existing = await getRecord<QuestLogRecord>(agent, did, COL.questLog, date);
    const log: QuestLogRecord = existing ?? {
      date,
      quests: [],
      activity: [],
      totalXpGained: 0,
      updatedAt: new Date().toISOString(),
    };
    // actionTypes 集合のいずれかが quest の expectedActionTypes / forbiddenActionTypes に
    // 含まれれば match。complex 行動 (引用 + 意見表明など) は両方カウント可。
    if (actionTypes.size > 0) {
      for (const q of log.quests) {
        if (q.completed) continue;
        const tmpl = templateById(q.templateId);
        if (!tmpl) continue;
        if (tmpl.type === 'restraint') {
          const violated = tmpl.forbiddenActionTypes?.some((t) => actionTypes.has(t)) ?? false;
          if (violated) {
            q.completed = false;
            q.xpAwarded = 0;
          }
          continue;
        }
        const matched = tmpl.expectedActionTypes?.some((t) => actionTypes.has(t)) ?? false;
        if (matched) {
          q.currentCount = q.currentCount + 1;
          incremented.push(q.templateId);
          if (q.currentCount >= q.requiredCount && q.requiredCount > 0) {
            q.completed = true;
            const xp = tmpl.xpRewardFn(q.currentCount);
            q.xpAwarded = xp;
            completed.push(q.templateId);
            xpGained += xp;
          }
        }
      }
    }
    const preview = trimmed.length > ACTIVITY_PREVIEW_LENGTH ? trimmed.slice(0, ACTIVITY_PREVIEW_LENGTH) + '…' : trimmed;
    const entry: ActivityEntry = {
      at: new Date().toISOString(),
      preview,
      action: actionType,
      actionTypes: [...actionTypes],
      incremented,
    };
    const prev = log.activity ?? [];
    log.activity = [...prev, entry].slice(-ACTIVITY_HISTORY_LIMIT);
    log.totalXpGained = (log.totalXpGained ?? 0) + xpGained;
    log.updatedAt = new Date().toISOString();
    await putRecord(agent, COL.questLog, date, log);
  }

  // 4) analysis を更新
  //   - cognitiveScores を α でブレンド (新しい投稿が少しずつ寄せる)
  //   - rpgStats は常に cognitiveScores から合成
  //   - ※ archetype は post ごとに自動更新しない (勝手な転職を防ぐ)。
  //     re-diagnosis (明示的な「もう一度調べる」) でのみ更新される。
  //   - playerLevel.xp は常に積む。jobLevel.xp は現 archetype のみ積む。
  //   - 日次ボーナス / streak は playerLevel で判定 (1 日 1 回の本体)。
  let updatedRpgStats: StatVector | undefined;
  let updatedCognitive: CognitiveScores | undefined;
  let finalJobLevel: JobLevelState | undefined;
  let finalPlayerLevel: PlayerLevelState | undefined;
  let jobLeveledUp: { from: number; to: number } | undefined;
  let finalPendingArchetype: Archetype | undefined;
  let finalPendingStreak: number | undefined;
  try {
    const analysis = await getRecord<DiagnosisResult>(agent, did, COL.analysis, 'self');
    if (analysis?.cognitiveScores) {
      const blended = postCognitive
        ? blendCognitive(analysis.cognitiveScores, postCognitive, COGNITIVE_BLEND_ALPHA)
        : analysis.cognitiveScores;
      const nextStats = cognitiveToRpg(blended);
      if (postCognitive) {
        updatedCognitive = blended;
        updatedRpgStats = nextStats;
      }

      // ── XP 計算 ─────────────────────────────
      const now = new Date();
      const nowIso = now.toISOString();
      const today = todayDateString();
      const oldPlayer: PlayerLevelState = analysis.playerLevel ?? { xp: 0, streakDays: 0 };
      const oldJob: JobLevelState = analysis.jobLevel ?? {
        archetype: analysis.archetype,
        xp: 0,
        joinedAt: analysis.analyzedAt,
      };

      // **XP の額はここで計算しない** (#551)。分類できたか / 日次ボーナスか / 連続何日目かは、
      // どれも client にしか分からない値だった = サーバーが検証できなかった。
      // いまはサーバーが**投稿の実在を確かめてから額を決める**ので、client は申告するだけ。
      // 連続日数もサーバーが数える (`GameState.streakDays`)。
      //
      // `playerLevel` は #507/#508 で戦闘力から外れており、ここは互換のため値を据え置く。
      finalPlayerLevel = oldPlayer;

      // **ジョブ XP は analysis に積まない** (#534)。記録先を権威 state (GameState.jobXp) に
      // 一本化したので、ここは**申告するだけ**。`analysis.jobLevel.xp` はベータ期間の記録として
      // 凍結し、/me の「これまでの記録」に出す。
      //
      // 申告は best-effort — 失敗しても投稿処理自体は続ける。edge が落ちている間に投稿しても
      // 投稿は成立させたい。**同じ投稿 URI で再申告しても二重に入らない**ので、将来
      // リトライを足すのも安全 (冪等キーはサーバー側が直近 200 件覚えている)。
      const nextJobLevel: JobLevelState = oldJob; // 凍結 (値を変えない)
      finalJobLevel = nextJobLevel;
      // 申告そのものは analysis を書いた**後**に行う (下の claim(): putRecord の後で await)。
      // 先に待つと、edge のタイムアウト (最大 8 秒) の間にタブを閉じられたとき、
      // 認知スコアのブレンド・streak・playerLevel の更新ごと失われる。
      claim = async () => {
        const arch = analysis.archetype;
        // **額は送らない。** サーバーが投稿の実在を確かめてから決める (#551)。
        // 落ちた申告の保留と出し直しは claimPostXp が持つ (#548)。ここで諦めると、
        // PDS の一時障害だけでその投稿の XP が永久に消える (申告は投稿時にしか走らないため)。
        const r = await claimPostXp((c) => serverClaimXp(agent, c), postUri ? { archetype: arch, postUri } : null);
        if (r && r.granted > 0) {
          // レベルアップ判定は**権威 state の値**で行う。
          const before = jobLevelFromXp(r.jobXp - r.granted, arch);
          const after = jobLevelFromXp(r.jobXp, arch);
          if (after > before) jobLeveledUp = { from: before, to: after };
          finalJobLevel = { archetype: arch, xp: r.jobXp, joinedAt: oldJob.joinedAt };
        }
      };

      // 解析を飛ばした (WebGPU 非対応) 投稿では、認知スコアも転職候補も書き換えない。
      if (postCognitive) {
        // 転職候補の検出: ブレンド後 cognitive から archetype を再判定し、現 archetype
        // と違えば pendingArchetype として保存 + 連続回数を積む。同じに戻ればクリア。
        const { archetype: candidate } = determineArchetype(blended);
        let nextPendingArchetype: Archetype | undefined;
        let nextPendingStreak: number | undefined;
        if (candidate && candidate !== analysis.archetype) {
          if (analysis.pendingArchetype === candidate) {
            nextPendingStreak = (analysis.pendingArchetypeStreak ?? 0) + 1;
          } else {
            nextPendingStreak = 1;
          }
          nextPendingArchetype = candidate;
        }
        finalPendingArchetype = nextPendingArchetype;
        finalPendingStreak = nextPendingStreak;

        await putRecord(agent, COL.analysis, 'self', {
          ...analysis,
          cognitiveScores: blended,
          rpgStats: nextStats,
          // archetype は固定 (post ごとには変えない)
          analyzedAt: analysis.analyzedAt,
          jobLevel: nextJobLevel,
          playerLevel: oldPlayer, // #551: XP は権威側。ここは据え置き
          ...(nextPendingArchetype
            ? { pendingArchetype: nextPendingArchetype, pendingArchetypeStreak: nextPendingStreak }
            : { pendingArchetype: undefined, pendingArchetypeStreak: undefined }),
        });
      }
      void nowIso;
    }
  } catch (e) {
    console.warn('analysis update failed', e);
  }
  // XP 申告は analysis を書き終えてから (上記の理由)。
  await claim();

  return {
    action,
    incremented,
    completed,
    xpGained,
    updatedRpgStats,
    updatedCognitive,
    jobLevel: finalJobLevel,
    playerLevel: finalPlayerLevel,
    jobLeveledUp,
    pendingArchetype: finalPendingArchetype,
    pendingArchetypeStreak: finalPendingStreak,
  };
}

/**
 * ユーザーが転職バナーの「転職する」ボタンを押したとき呼ぶ。
 * - analysis.archetype を pendingArchetype に切り替え
 * - jobLevel を新 archetype で xp=0 から再スタート (playerLevel は維持)
 * - pendingArchetype / Streak をクリア
 */
export async function confirmJobChange(agent: Agent, did: string, newArchetype: Archetype): Promise<DiagnosisResult | null> {
  const analysis = await getRecord<DiagnosisResult>(agent, did, COL.analysis, 'self');
  if (!analysis) return null;
  // **`jobLevel` は触らない** (#534)。ここはベータ期間の記録として凍結された値で、
  // 上書きすると `/me` の「ベータ期間の記録」が消える (PDS ごと書き換わるので復元不能)。
  // 現職の XP は権威 state (`GameState.jobXp[archetype]`) が持っており、archetype キーなので
  // 転職しても前職のぶんは残り、戻れば復活する (#531 が構造的に解決)。
  const next: DiagnosisResult = {
    ...analysis,
    archetype: newArchetype,
  };
  delete (next as Partial<DiagnosisResult>).pendingArchetype;
  delete (next as Partial<DiagnosisResult>).pendingArchetypeStreak;
  await putRecord(agent, COL.analysis, 'self', next);
  return next;
}

/**
 * 管理者用: 自分のジョブを即座に任意の archetype に切り替える (dev の /admin から)。
 * 本人の PDS (analysis/self) を本人トークンで書く。
 *
 * **レベルはここでは動かない** (#534)。XP の記録先を権威 state に一本化したので、
 * `analysis.jobLevel.xp` はベータ期間の記録として凍結され、成長には効かない。
 * レベルの設定は `serverAdminSetJobLevel` (edge の ADMIN_DIDS ゲート付きエンドポイント) が担当し、
 * 呼び出し側 (job-change-admin) が職とレベルの両方を順に設定する。
 *
 * `jobLevel` を書き換えないのは、そこが `/me` の「ベータ期間の記録」の表示元だから
 * (上書きすると PDS ごと消えて復元できない)。
 */
export async function adminSetJob(agent: Agent, did: string, newArchetype: Archetype): Promise<DiagnosisResult | null> {
  const analysis = await getRecord<DiagnosisResult>(agent, did, COL.analysis, 'self');
  if (!analysis) return null;
  const next: DiagnosisResult = {
    ...analysis,
    archetype: newArchetype,
  };
  delete (next as Partial<DiagnosisResult>).pendingArchetype;
  delete (next as Partial<DiagnosisResult>).pendingArchetypeStreak;
  await putRecord(agent, COL.analysis, 'self', next);
  return next;
}

/** ユーザーが「このまま」を押したとき呼ぶ。pending をクリアするが、次の投稿で再度出る可能性あり。 */
export async function dismissPendingArchetype(agent: Agent, did: string): Promise<void> {
  const analysis = await getRecord<DiagnosisResult>(agent, did, COL.analysis, 'self');
  if (!analysis) return;
  const next: Partial<DiagnosisResult> = { ...analysis };
  delete next.pendingArchetype;
  delete next.pendingArchetypeStreak;
  await putRecord(agent, COL.analysis, 'self', next);
}

/** dateA (YYYY-MM-DD) が dateB の前日かどうか。 */
function isYesterday(dateA: string, dateB: string): boolean {
  const a = new Date(dateA + 'T00:00:00');
  const b = new Date(dateB + 'T00:00:00');
  const diff = b.getTime() - a.getTime();
  return diff > 0 && diff <= 1000 * 60 * 60 * 24 * DAILY_BONUS_DAY_MARGIN_FACTOR;
}

function blendCognitive(
  existing: CognitiveScores,
  post: CognitiveScores,
  alpha: number,
): CognitiveScores {
  const out = {} as CognitiveScores;
  for (const fn of COGNITIVE_FUNCTIONS) {
    const e = existing[fn] ?? 0;
    const p = post[fn] ?? 0;
    out[fn] = Math.round(alpha * e + (1 - alpha) * p);
  }
  return out;
}

/** HomeSummary が生成したクエストを questLog レコードに初期化する。 */
export async function ensureTodayQuestLog(
  agent: Agent,
  did: string,
  quests: Quest[],
): Promise<QuestLogRecord> {
  const date = todayDateString();
  const existing = await getRecord<QuestLogRecord>(agent, did, COL.questLog, date);
  if (existing) return existing;
  const record: QuestLogRecord = {
    date,
    quests: quests.map<QuestLogEntry>((q) => ({
      id: q.id,
      templateId: q.templateId,
      type: q.type,
      targetStat: q.targetStat,
      requiredCount: q.requiredCount,
      currentCount: q.currentCount,
      completed: false,
    })),
    totalXpGained: 0,
    updatedAt: new Date().toISOString(),
  };
  await putRecord(agent, COL.questLog, date, record);
  return record;
}

export async function loadTodayQuestLog(
  agent: Agent,
  did: string,
): Promise<QuestLogRecord | null> {
  const date = todayDateString();
  return getRecord<QuestLogRecord>(agent, did, COL.questLog, date);
}
